// library imports
import { Table, Button } from "antd";
import { useState, useEffect, useContext } from "react";
import {
  LoadingOutlined,
  VerticalAlignBottomOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// style imports
import "../../styles/SystemReport/AnnualReportContent.css";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";
// component imports
import { HorizontalDateMonthYearFilter } from "../common/DateFilter";
// config imports
import { MICROGRID_SITE_IDS } from "../../configs/configs";

const { Column, ColumnGroup } = Table;

function AnnualReportContent() {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const [siteCommissioningDate, setSiteCommissioningDate] = useState(null);

  const [dateSelection, setDateSelection] = useState(dayjs());
  const [data, setData] = useState([]);

  const [includePvSyst, setIncludePvSyst] = useState(false);

  const formatToTwoDecimalPlaces = (value) => {
    if (value === undefined || value === null || value === "-") {
      return value;
    }
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  /** API to get annual report */
  function getAnnualReportData() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "annual_report/getAnnualReportData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        date_graph: dateSelection.startOf("year").format("YYYY-MM-DD HH:mm:ss"),
      },

      onSuccess: (response) => {
        setData(response.data.data);
        setIncludePvSyst(response.data.include_pv_syst);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get annual report data.",
          description: response.statusText,
        });
      },
      onFinish: () => {
        setLoading(false);
      },
    });
  }

  /** API to download annual report */
  function downloadAnnualReport() {
    setSaveLoading(true);

    fetchWrapper({
      endpoint_url: "annual_report/downloadAnnualReport",
      method: "POST",
      json_output: false,
      body: {
        table_data: data,
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        if (response.headers.get("content-type") !== "application/json") {
          // no issue with the response. returned a spreadsheet
          response.blob().then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `AnnualReport_${dateSelection.format("YYYY")}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
          });
        }
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to download file",
        });
      },

      onFinish: () => {
        setSaveLoading(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "common/getSiteCommissioningDate",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
      },

      onSuccess: (response) => {
        setSiteCommissioningDate(response.data.site_commissioning_date);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get site commissioning date.",
          description: response.statusText,
        });
      },
    });
  }

  useEffect(() => {
    if (siteSelected) {
      getAnnualReportData();
    }
  }, [siteSelected, dateSelection]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }
  }, [siteSelected]);

  return (
    <>
      <div className="annual-report-date-selection">
        <Button
          className="annual-report-download-button"
          onClick={() => {
            downloadAnnualReport();
          }}
          disabled={loading}
        >
          {saveLoading ? <LoadingOutlined /> : <VerticalAlignBottomOutlined />}
        </Button>
        <HorizontalDateMonthYearFilter
          startDateSelection={dateSelection}
          setStartDateSelection={setDateSelection}
          onFilter={() => {}}
          pickerType={"year"}
          showTitle={false}
          minDate={dayjs(siteCommissioningDate, "YYYY-MM-DD")}
          maxDate={dayjs()}
        />
      </div>
      {MICROGRID_SITE_IDS.includes(siteSelected) ? (
        <Table
          size="small"
          bordered
          dataSource={data}
          scroll={{ x: 800 }}
          loading={loading}
        >
          <Column title="Month" dataIndex="month" key="month" />
          <Column title="Load Consumption" dataIndex="Load" key="Load" />
          <ColumnGroup title="Energy Supply">
            <Column title="Solar Generation" dataIndex="Solar" key="Solar" />
            <Column title="Genset Supply" dataIndex="Genset" key="Genset" />
            <Column
              title="BESS Supply"
              dataIndex="BessDischarge"
              key="BessDischarge"
            />
          </ColumnGroup>
          <Column title="BESS Charge" dataIndex="BessCharge" key="BessCharge" />
        </Table>
      ) : includePvSyst ? (
        <Table
          size="small"
          bordered
          dataSource={data}
          scroll={{ x: 800 }}
          loading={loading}
        >
          <Column title="Month" dataIndex="month" key="month" />
          <ColumnGroup title="Energy Generation">
            <Column
              title="Actual"
              dataIndex="actualEnergyGeneration"
              key="actualEnergyGeneration"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PVSyst"
              dataIndex="pvSystEnergyGeneration"
              key="pvSystEnergyGeneration"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Deviation (%)"
              dataIndex="deviationEnergyGeneration"
              key="deviationEnergyGeneration"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
          <ColumnGroup title="Energy Utilization">
            <Column
              title="PV Self Consumption (kWh)"
              dataIndex="pvSelfConsumption"
              key="pvSelfConsumption"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Self Consumption Ratio"
              dataIndex="selfConsumptionRatio"
              key="selfConsumptionRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PV Export (kWh)"
              dataIndex="pvExport"
              key="pvExport"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PV Energy Export Ratio"
              dataIndex="pvEnergyExportRatio"
              key="pvEnergyExportRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Import From Grid (kWh)"
              dataIndex="importFromGrid"
              key="importFromGrid"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
          <ColumnGroup title="Solar Irradiation (kWh/m²)">
            <Column
              title="Actual"
              dataIndex="actualSolarIrradiation"
              key="actualSolarIrradiation"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PVSyst"
              dataIndex="pvSystGlobHor"
              key="pvSystGlobHor"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Deviation (%)"
              dataIndex="deviationSolarIrradiation"
              key="deviationSolarIrradiation"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
          <ColumnGroup title="Performance Ratio">
            <Column
              title="Actual"
              dataIndex="actualPerformanceRatio"
              key="actualPerformanceRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PVSyst"
              dataIndex="pvSystPerformanceRatio"
              key="pvSystPerformanceRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Deviation (%)"
              dataIndex="deviationPerformanceRatio"
              key="deviationPerformanceRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
          <ColumnGroup title="Specific Yield">
            <Column
              title="Actual"
              dataIndex="actualSpecificYield"
              key="actualSpecificYield"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PVSyst"
              dataIndex="pvSystSpecificYield"
              key="pvSystSpecificYield"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Deviation (%)"
              dataIndex="deviationSpecificYield"
              key="deviationSpecificYield"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
        </Table>
      ) : (
        <Table
          size="small"
          bordered
          dataSource={data}
          scroll={{ x: 800 }}
          loading={loading}
        >
          <Column title="Month" dataIndex="month" key="month" />
          <Column
            title="Energy Generation (kWh)"
            dataIndex="actualEnergyGeneration"
            key="actualEnergyGeneration"
            render={(value) => formatToTwoDecimalPlaces(value)}
          />

          <ColumnGroup title="Energy Utilization (kWh)">
            <Column
              title="PV Self Consumption (kWh)"
              dataIndex="pvSelfConsumption"
              key="pvSelfConsumption"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Self Consumption Ratio"
              dataIndex="selfConsumptionRatio"
              key="selfConsumptionRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PV Export (kWh)"
              dataIndex="pvExport"
              key="pvExport"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="PV Energy Export Ratio"
              dataIndex="pvEnergyExportRatio"
              key="pvEnergyExportRatio"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
            <Column
              title="Import From Grid (kWh)"
              dataIndex="importFromGrid"
              key="importFromGrid"
              render={(value) => formatToTwoDecimalPlaces(value)}
            />
          </ColumnGroup>
          <Column
            title="Solar Irradiation (kWh/m²)"
            dataIndex="actualSolarIrradiation"
            key="actualSolarIrradiation"
            render={(value) => formatToTwoDecimalPlaces(value)}
          />

          <Column
            title="Performance Ratio"
            dataIndex="actualPerformanceRatio"
            key="actualPerformanceRatio"
            render={(value) => formatToTwoDecimalPlaces(value)}
          />

          <Column
            title="Specific Yield (kWh/kWp)"
            dataIndex="actualSpecificYield"
            key="actualSpecificYield"
            render={(value) => formatToTwoDecimalPlaces(value)}
          />
        </Table>
      )}
    </>
  );
}

export default AnnualReportContent;
